<template>
  <div class="svg-editor-container">
    <!-- 左侧：文档列表 -->
    <aside class="doc-sidebar">
      <div class="sidebar-header">
        <span class="sidebar-title">SVG 文档</span>
        <el-button
          type="primary"
          size="mini"
          icon="el-icon-plus"
          :loading="creating"
          @click="handleCreate"
        >
          新建
        </el-button>
      </div>

      <div v-loading="listLoading" class="doc-list">
        <div
          v-for="doc in docList"
          :key="doc.id"
          :class="['doc-item', { 'is-active': currentDoc && currentDoc.id === doc.id }]"
          @click="handleOpen(doc)"
        >
          <div class="doc-item__main">
            <span class="doc-item__title" :title="doc.title">{{ doc.title }}</span>
            <span class="doc-item__meta">
              {{ doc.width && doc.height ? `${doc.width} × ${doc.height}` : '未标注尺寸' }}
              · {{ formatTime(doc.updatedAt) }}
            </span>
          </div>
          <el-button
            class="doc-item__delete"
            type="text"
            icon="el-icon-delete"
            @click.stop="handleDelete(doc)"
          />
        </div>

        <div v-if="!listLoading && docList.length === 0" class="doc-empty">
          还没有文档，点右上角「新建」开始画图
        </div>
      </div>
    </aside>

    <!-- 右侧：编辑区 -->
    <section class="editor-panel">
      <div class="editor-toolbar">
        <div class="toolbar-left">
          <el-input
            v-model="docTitle"
            class="title-input"
            size="small"
            maxlength="60"
            placeholder="未命名文档"
            :disabled="!currentDoc"
            @input="markDirty"
          />
          <el-tag v-if="dirty" size="mini" type="warning" effect="plain">未保存</el-tag>
        </div>

        <div class="toolbar-right">
          <el-button
            size="small"
            icon="el-icon-check"
            type="primary"
            :disabled="!currentDoc || saving || !editorReady"
            :loading="saving"
            @click="handleSave"
          >
            保存
          </el-button>
          <el-dropdown
            :disabled="!currentDoc || !editorReady"
            trigger="click"
            @command="handleExport"
          >
            <el-button size="small" icon="el-icon-download">
              导出<i class="el-icon-arrow-down el-icon--right" />
            </el-button>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item command="PNG">导出 PNG</el-dropdown-item>
              <el-dropdown-item command="PDF">导出 PDF</el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
          <el-button
            size="small"
            icon="el-icon-refresh-right"
            :disabled="!currentDoc"
            @click="handleReload"
          >
            重新载入
          </el-button>
        </div>
      </div>

      <div class="editor-stage">
        <iframe
          v-if="currentDoc"
          ref="editorFrame"
          class="editor-frame"
          :src="editorSrc"
          title="SVG 编辑器"
          @load="handleFrameLoad"
        />

        <div v-else class="editor-placeholder">
          <i class="el-icon-picture-outline" />
          <p>从左侧选择一个文档，或新建一个开始画图</p>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { Message, MessageBox } from 'element-ui'
import {
  fetchSvgDocList,
  fetchSvgDocDetail,
  createSvgDoc,
  updateSvgDoc,
  deleteSvgDoc,
} from '@/api/modules/svgDoc'

/** 宿主页发给 iframe 的消息标记（与 svgedit/index.html 中的 HOST_SOURCE 对应）。 */
const HOST_SOURCE = 'skill-library'
/** iframe 发给宿主页的消息标记。 */
const EDITOR_SOURCE = 'svgedit'
/** 等待 iframe 回复的默认超时时间（毫秒）。 */
const REQUEST_TIMEOUT = 20000
/** 新建文档时灌入编辑器的空白画布。 */
const BLANK_SVG = `<svg width="1024" height="768" xmlns="http://www.w3.org/2000/svg">
  <rect width="100%" height="100%" fill="#ffffff"/>
</svg>`

/**
 * 推导编辑器 iframe 地址，兼容部署在子路径的情况。
 * @returns {string} 形如 `/svgedit/index.html`
 */
function resolveEditorSrc() {
  const { base } = process.env
  const root = !base || base === '/'
    ? '/'
    : `${base.replace(/^\/+/, '/').replace(/\/+$/, '')}/`
  return `${root}svgedit/index.html`
}

export default {
  name: 'SvgEditor',

  data() {
    return {
      // 编辑器 iframe 地址（兼容子路径部署）
      editorSrc: resolveEditorSrc(),
      // 文档列表（仅元信息）
      docList: [],
      listLoading: false,
      creating: false,
      // 当前打开的文档（含 svgString）
      currentDoc: null,
      docTitle: '',
      dirty: false,
      saving: false,
      // 编辑器 iframe 是否就绪
      editorReady: false,
      // requestId -> { resolve, reject, timer }
      pendingRequests: new Map(),
      requestSeq: 0,
    }
  },

  mounted() {
    window.addEventListener('message', this.handleEditorMessage)
    this.loadDocList()
  },

  beforeDestroy() {
    window.removeEventListener('message', this.handleEditorMessage)
    // 清掉所有未完成的请求，避免组件销毁后回调还往已卸载的实例里写数据
    this.pendingRequests.forEach(({ timer, reject }) => {
      clearTimeout(timer)
      reject(new Error('页面已关闭'))
    })
    this.pendingRequests.clear()
  },

  methods: {
    /**
     * 拉取文档列表。
     * @returns {Promise<void>}
     */
    async loadDocList() {
      this.listLoading = true
      try {
        this.docList = await fetchSvgDocList()
      } catch (error) {
        this.docList = []
      } finally {
        this.listLoading = false
      }
    },

    /**
     * 把时间戳格式化为「今天 HH:mm」/「MM-DD HH:mm」。
     * @param {string} value ISO 时间字符串
     * @returns {string} 展示用时间
     */
    formatTime(value) {
      if (!value) {
        return '-'
      }
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) {
        return '-'
      }
      const pad = (num) => String(num).padStart(2, '0')
      const now = new Date()
      const isToday =
        date.getFullYear() === now.getFullYear() &&
        date.getMonth() === now.getMonth() &&
        date.getDate() === now.getDate()
      const clock = `${pad(date.getHours())}:${pad(date.getMinutes())}`
      return isToday ? `今天 ${clock}` : `${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${clock}`
    },

    /**
     * 标记当前文档有未保存改动。
     * @returns {void}
     */
    markDirty() {
      if (this.currentDoc) {
        this.dirty = true
      }
    },

    /**
     * 新建文档：建一条空记录并直接打开。
     * @returns {Promise<void>}
     */
    async handleCreate() {
      if (!(await this.confirmLeaveDirty())) {
        return
      }

      this.creating = true
      try {
        const created = await createSvgDoc({
          title: `未命名文档 ${this.docList.length + 1}`,
          svgString: BLANK_SVG,
        })
        Message.success('已创建文档')
        await this.loadDocList()
        await this.openDoc(created.id, created.title, BLANK_SVG)
      } catch (error) {
        // request 拦截器已弹出错误提示
      } finally {
        this.creating = false
      }
    },

    /**
     * 打开列表里的文档。
     * @param {Object} doc 列表项
     * @returns {Promise<void>}
     */
    async handleOpen(doc) {
      if (this.currentDoc && this.currentDoc.id === doc.id) {
        return
      }
      if (!(await this.confirmLeaveDirty())) {
        return
      }

      try {
        const detail = await fetchSvgDocDetail(doc.id)
        await this.openDoc(detail.id, detail.title, detail.svgString)
      } catch (error) {
        // request 拦截器已弹出错误提示
      }
    },

    /**
     * 把文档灌进编辑器。
     * @param {string} id 文档 id
     * @param {string} title 文档标题
     * @param {string} svgString SVG 文本
     * @returns {Promise<void>}
     */
    async openDoc(id, title, svgString) {
      const needReload = !this.currentDoc

      this.currentDoc = { id, title, svgString }
      this.docTitle = title
      this.dirty = false

      if (needReload) {
        // iframe 首次渲染，等 load 事件后由 handleFrameLoad 灌内容
        return
      }

      // iframe 已存在：直接推一份新内容过去
      if (this.editorReady) {
        await this.pushContent(svgString, title)
      }
    },

    /**
     * iframe 加载完成：发起初始化握手。
     * @returns {Promise<void>}
     */
    async handleFrameLoad() {
      // 注意：iframe 内部 init 是异步的，load 事件只保证 DOM 就绪，
      // 真正的可用信号是 editor:ready，所以这里先标记为未就绪。
      this.editorReady = false
    },

    /**
     * 等编辑器 ready 后把当前文档灌进去。
     * @returns {Promise<void>}
     */
    async onEditorReady() {
      this.editorReady = true
      if (this.currentDoc) {
        await this.pushContent(this.currentDoc.svgString, this.currentDoc.title)
      }
    },

    /**
     * 向编辑器下发内容。
     * @param {string} svgString SVG 文本
     * @param {string} title 文档标题
     * @returns {Promise<void>}
     */
    async pushContent(svgString, title) {
      try {
        const result = await this.sendToEditor('host:init', { svgString, title })
        if (result && result.ok === false) {
          Message.error(result.message || '载入 SVG 失败')
        }
      } catch (error) {
        Message.error(error.message || '编辑器无响应')
      }
    },

    /**
     * 保存当前文档（有 id 走更新，否则新建）。
     * @returns {Promise<void>}
     */
    async handleSave() {
      if (!this.currentDoc) {
        return
      }
      const title = this.docTitle.trim()
      if (!title) {
        Message.warning('请先填写文档名称')
        return
      }

      this.saving = true
      try {
        const current = await this.sendToEditor('host:getSvg')
        const svgString = current && current.svgString
        if (!svgString) {
          Message.error('读取画布内容失败')
          return
        }

        await updateSvgDoc(this.currentDoc.id, { title, svgString })

        this.currentDoc.svgString = svgString
        this.currentDoc.title = title
        this.dirty = false
        Message.success('保存成功')
        await this.loadDocList()
      } catch (error) {
        // request 拦截器已弹出错误提示
      } finally {
        this.saving = false
      }
    },

    /**
     * 重新从服务端载入当前文档（丢弃本地改动）。
     * @returns {Promise<void>}
     */
    async handleReload() {
      if (!this.currentDoc) {
        return
      }
      try {
        await MessageBox.confirm('重新载入会丢弃当前未保存的改动，确定继续？', '提示', {
          type: 'warning',
        })
      } catch (error) {
        return
      }

      try {
        const detail = await fetchSvgDocDetail(this.currentDoc.id)
        this.currentDoc.svgString = detail.svgString
        this.currentDoc.title = detail.title
        this.docTitle = detail.title
        this.dirty = false
        await this.pushContent(detail.svgString, detail.title)
        Message.success('已重新载入')
      } catch (error) {
        // request 拦截器已弹出错误提示
      }
    },

    /**
     * 导出 PNG / PDF。
     * @param {'PNG'|'PDF'} format 导出格式
     * @returns {Promise<void>}
     */
    async handleExport(format) {
      if (!this.currentDoc) {
        return
      }
      const loading = this.$loading({
        lock: true,
        text: `正在导出 ${format}…`,
      })
      try {
        const result = await this.sendToEditor('host:export', { format }, 60000)
        if (result && result.blob) {
          this.downloadBlob(result.blob, result.filename || `${this.docTitle}.${format.toLowerCase()}`)
          Message.success(`${format} 导出成功`)
        } else {
          Message.error('导出失败，未拿到文件内容')
        }
      } catch (error) {
        Message.error(error.message || '导出失败')
      } finally {
        loading.close()
      }
    },

    /**
     * 触发浏览器下载。
     * @param {Blob} blob 文件内容
     * @param {string} filename 文件名
     * @returns {void}
     */
    downloadBlob(blob, filename) {
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      // 稍后回收，避免 Safari 下载未开始就失效
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    },

    /**
     * 删除文档。
     * @param {Object} doc 列表项
     * @returns {Promise<void>}
     */
    async handleDelete(doc) {
      try {
        await MessageBox.confirm(`确定删除「${doc.title}」？删除后不可恢复。`, '删除确认', {
          type: 'warning',
        })
      } catch (error) {
        return
      }

      try {
        await deleteSvgDoc(doc.id)
        Message.success('已删除')
        if (this.currentDoc && this.currentDoc.id === doc.id) {
          this.currentDoc = null
          this.docTitle = ''
          this.dirty = false
          this.editorReady = false
        }
        await this.loadDocList()
      } catch (error) {
        // request 拦截器已弹出错误提示
      }
    },

    /**
     * 切换文档前询问是否丢弃未保存改动。
     * @returns {Promise<boolean>} 是否可以继续
     */
    async confirmLeaveDirty() {
      if (!this.dirty) {
        return true
      }
      try {
        await MessageBox.confirm('当前文档有未保存的改动，确定丢弃？', '提示', {
          type: 'warning',
        })
        return true
      } catch (error) {
        return false
      }
    },

    /**
     * 处理编辑器发来的消息。
     * @param {MessageEvent} event 消息事件
     * @returns {void}
     */
    handleEditorMessage(event) {
      const frame = this.$refs.editorFrame
      // 只接受自己这个 iframe 的消息，避免同域其他 iframe 串话
      if (!frame || event.source !== frame.contentWindow) {
        return
      }
      const data = event.data
      if (!data || data.source !== EDITOR_SOURCE) {
        return
      }

      switch (data.type) {
        case 'editor:ready':
          this.onEditorReady()
          break
        case 'editor:changed':
          if (data.payload && data.payload.undoStackSize > 0) {
            this.markDirty()
          }
          break
        case 'editor:reply':
        case 'editor:exported': {
          const pending = this.pendingRequests.get(data.requestId)
          if (!pending) {
            return
          }
          clearTimeout(pending.timer)
          this.pendingRequests.delete(data.requestId)
          if (data.type === 'editor:exported') {
            pending.resolve(data.payload)
          } else if (data.ok) {
            pending.resolve(data.payload)
          } else {
            pending.reject(new Error(data.message || '编辑器处理失败'))
          }
          break
        }
        default:
          break
      }
    },

    /**
     * 给编辑器发一条消息并等待回复。
     * @param {string} type 消息类型
     * @param {Object} [payload] 载荷
     * @param {number} [timeout] 超时时间（毫秒）
     * @returns {Promise<Object>} 编辑器返回的 payload
     */
    sendToEditor(type, payload, timeout = REQUEST_TIMEOUT) {
      return new Promise((resolve, reject) => {
        const frame = this.$refs.editorFrame
        if (!frame || !frame.contentWindow) {
          reject(new Error('编辑器尚未加载完成'))
          return
        }

        this.requestSeq += 1
        const requestId = `req_${this.requestSeq}`

        const timer = setTimeout(() => {
          this.pendingRequests.delete(requestId)
          reject(new Error('编辑器响应超时，请重试'))
        }, timeout)

        this.pendingRequests.set(requestId, { resolve, reject, timer })

        frame.contentWindow.postMessage(
          { source: HOST_SOURCE, type, requestId, payload },
          '*'
        )
      })
    },
  },
}
</script>

<style scoped>
.svg-editor-container {
  display: flex;
  height: 100%;
  min-height: 0;
  gap: var(--spacing-md, 16px);
}

/* ===== 左侧文档列表 ===== */
.doc-sidebar {
  width: 240px;
  flex: 0 0 240px;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: var(--bg-color-white, #fff);
  border-radius: var(--border-radius-md, 8px);
  box-shadow: var(--shadow-sm, 0 1px 3px rgba(0, 0, 0, 0.12));
  overflow: hidden;
}

.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-sm, 8px) var(--spacing-md, 16px);
  border-bottom: 1px solid var(--border-color-lighter, #ebeef5);
}

.sidebar-title {
  font-size: var(--font-size-base, 14px);
  font-weight: 600;
  color: var(--color-text-primary, #303133);
}

.doc-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: var(--spacing-sm, 8px);
}

.doc-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  border-radius: var(--border-radius-base, 4px);
  cursor: pointer;
  transition: background-color var(--transition-duration-fast, 0.15s);
}

.doc-item:hover {
  background: var(--bg-color-info, #f4f4f5);
}

.doc-item.is-active {
  background: rgba(64, 158, 255, 0.1);
}

.doc-item__main {
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 2px;
}

.doc-item__title {
  font-size: var(--font-size-sm, 13px);
  color: var(--color-text-primary, #303133);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.doc-item.is-active .doc-item__title {
  color: var(--color-primary, #409eff);
  font-weight: 600;
}

.doc-item__meta {
  font-size: var(--font-size-xs, 12px);
  color: var(--color-text-secondary, #909399);
}

.doc-item__delete {
  padding: 0;
  opacity: 0;
  transition: opacity var(--transition-duration-fast, 0.15s);
}

.doc-item:hover .doc-item__delete {
  opacity: 1;
}

.doc-item__delete:hover {
  color: var(--color-danger, #f56c6c);
}

.doc-empty {
  padding: var(--spacing-lg, 20px) var(--spacing-sm, 8px);
  font-size: var(--font-size-xs, 12px);
  line-height: 1.6;
  color: var(--color-text-secondary, #909399);
  text-align: center;
}

/* ===== 右侧编辑区 ===== */
.editor-panel {
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: var(--bg-color-white, #fff);
  border-radius: var(--border-radius-md, 8px);
  box-shadow: var(--shadow-sm, 0 1px 3px rgba(0, 0, 0, 0.12));
  overflow: hidden;
}

.editor-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-md, 16px);
  padding: var(--spacing-sm, 8px) var(--spacing-md, 16px);
  border-bottom: 1px solid var(--border-color-lighter, #ebeef5);
  flex: 0 0 auto;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm, 8px);
  min-width: 0;
}

.title-input {
  width: 240px;
}

.toolbar-right {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm, 8px);
  flex: 0 0 auto;
}

.editor-stage {
  flex: 1;
  min-height: 0;
  position: relative;
  background: var(--bg-color-page, #f0f2f5);
}

.editor-frame {
  display: block;
  width: 100%;
  height: 100%;
  border: 0;
}

.editor-placeholder {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm, 8px);
  color: var(--color-text-secondary, #909399);
  font-size: var(--font-size-sm, 13px);
}

.editor-placeholder i {
  font-size: 48px;
  opacity: 0.4;
}
</style>
